import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Star, 
  Check, 
  X,
  Trash2, 
  Loader2,
  AlertCircle,
  Award,
  Mail,
  Send,
  Filter,
  Globe,
  Copy,
  QrCode,
  Printer,
  Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGE_NAMES = {
  es: 'Español',
  en: 'English',
  fr: 'Français',
  zh: '中文',
  ar: 'العربية',
  unknown: 'No identificado'
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  featured: 'bg-purple-100 text-purple-700'
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  featured: 'Destacada'
};

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const qrRef = useRef(null);
  const [emailForm, setEmailForm] = useState({ client_name: '', client_email: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [statusFilter, languageFilter]);

  const fetchReviews = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      let url = `${API}/admin/reviews`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (languageFilter !== 'all') params.append('language', languageFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      toast.error('Error al cargar las valoraciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.reviews);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const updateReviewStatus = async (reviewId, newStatus) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(
        `${API}/admin/reviews/${reviewId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Valoración ${STATUS_LABELS[newStatus].toLowerCase()}`);
      fetchReviews();
      fetchStats();
    } catch (err) {
      toast.error('Error al actualizar el estado');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta valoración?')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Valoración eliminada');
      fetchReviews();
      fetchStats();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const sendReviewRequest = async () => {
    if (!emailForm.client_name || !emailForm.client_email) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSendingEmail(true);
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.post(
        `${API}/admin/reviews/send-request`,
        emailForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'sent') {
        toast.success(`Email enviado a ${emailForm.client_email}`);
      } else {
        toast.error(`Error al enviar: ${response.data.status}`);
      }
      
      setShowEmailModal(false);
      setEmailForm({ client_name: '', client_email: '' });
    } catch (err) {
      toast.error('Error al enviar la solicitud');
    } finally {
      setSendingEmail(false);
    }
  };

  const copyReviewLink = () => {
    const baseUrl = window.location.origin;
    navigator.clipboard.writeText(`${baseUrl}/review`);
    toast.success('Enlace copiado');
  };

  const reviewUrl = `${window.location.origin}/review`;

  const handlePrintQR = () => {
    const printContent = qrRef.current;
    if (!printContent) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>QR Code - Madrid Tuk Tuk Tour</title>
      <style>
        body { display:flex; justify-content:center; align-items:center; min-height:100vh; margin:0; font-family:system-ui,sans-serif; }
        .container { text-align:center; padding:40px; }
        h1 { font-size:28px; color:#1C1917; margin-bottom:8px; }
        p { font-size:16px; color:#57534E; margin-bottom:24px; }
        .url { font-size:12px; color:#78716C; margin-top:16px; }
      </style></head><body>
      <div class="container">
        <h1>Madrid Tuk Tuk Tour</h1>
        <p>Deja tu valoracion</p>
        ${printContent.querySelector('svg').outerHTML}
        <p class="url">${reviewUrl}</p>
      </div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      canvas.width = 600;
      canvas.height = 750;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 600, 750);
      ctx.drawImage(img, 50, 50, 500, 500);
      ctx.fillStyle = '#1C1917';
      ctx.font = 'bold 28px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('Madrid Tuk Tuk Tour', 300, 600);
      ctx.fillStyle = '#57534E';
      ctx.font = '18px system-ui';
      ctx.fillText('Deja tu valoracion', 300, 635);
      ctx.fillStyle = '#78716C';
      ctx.font = '13px system-ui';
      ctx.fillText(reviewUrl, 300, 720);
      const link = document.createElement('a');
      link.download = 'madrid-tuktuk-qr.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const pendingReviews = reviews.filter(r => r.status === 'pending');
  const otherReviews = reviews.filter(r => r.status !== 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-reviews">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-[#1C1917]">Valoraciones</h1>
          <p className="text-[#57534E]">Gestiona las opiniones de los clientes</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowQRModal(true)}
            className="rounded-full"
            data-testid="show-qr-btn"
          >
            <QrCode className="h-4 w-4 mr-2" />
            Codigo QR
          </Button>
          <Button
            variant="outline"
            onClick={copyReviewLink}
            className="rounded-full"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copiar enlace
          </Button>
          <Button
            onClick={() => setShowEmailModal(true)}
            className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full"
            data-testid="send-review-request-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Enviar solicitud
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-yellow-700">{stats.pending || 0}</div>
              <div className="text-sm text-yellow-600">Pendientes</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-green-700">{stats.approved || 0}</div>
              <div className="text-sm text-green-600">Aprobadas</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-purple-700">{stats.featured || 0}</div>
              <div className="text-sm text-purple-600">Destacadas</div>
            </CardContent>
          </Card>
          <Card className="bg-[#FDFBF7] border-[#E5E5E5]">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-[#1C1917]">{stats.total || 0}</div>
              <div className="text-sm text-[#57534E]">Total</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#57534E]" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="approved">Aprobada</SelectItem>
                <SelectItem value="rejected">Rechazada</SelectItem>
                <SelectItem value="featured">Destacada</SelectItem>
              </SelectContent>
            </Select>
            <Select value={languageFilter} onValueChange={setLanguageFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Idioma" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los idiomas</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="zh">中文</SelectItem>
                <SelectItem value="ar">العربية</SelectItem>
                <SelectItem value="unknown">No identificado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Pending Reviews - Priority */}
      {pendingReviews.length > 0 && (
        <Card className="mb-8 border-yellow-200 bg-yellow-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="h-5 w-5" />
              Valoraciones Pendientes de Moderación ({pendingReviews.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  onUpdateStatus={updateReviewStatus}
                  onDelete={deleteReview}
                  isPending
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Other Reviews */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-[#C2410C]" />
            Todas las Valoraciones ({otherReviews.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {otherReviews.length === 0 ? (
            <p className="text-center text-[#57534E] py-8">
              No hay valoraciones que mostrar con los filtros seleccionados
            </p>
          ) : (
            <div className="space-y-4">
              {otherReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  onUpdateStatus={updateReviewStatus}
                  onDelete={deleteReview}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Review Request Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#C2410C]" />
              Enviar solicitud de valoración
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-[#57534E]">
              Envía un email al cliente con el enlace para dejar su valoración.
            </p>
            <div>
              <Label htmlFor="client_name">Nombre del cliente</Label>
              <Input
                id="client_name"
                value={emailForm.client_name}
                onChange={(e) => setEmailForm({...emailForm, client_name: e.target.value})}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="client_email">Email</Label>
              <Input
                id="client_email"
                type="email"
                value={emailForm.client_email}
                onChange={(e) => setEmailForm({...emailForm, client_email: e.target.value})}
                placeholder="john@email.com"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowEmailModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={sendReviewRequest}
                disabled={sendingEmail}
                className="flex-1 bg-[#C2410C] hover:bg-[#9A3412]"
              >
                {sendingEmail ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Enviar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <QrCode className="h-5 w-5 text-[#C2410C]" />
              Codigo QR para valoraciones
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6" ref={qrRef}>
            <QRCodeSVG
              value={reviewUrl}
              size={256}
              level="H"
              fgColor="#1C1917"
              bgColor="#FFFFFF"
              includeMargin
            />
            <p className="text-sm text-[#57534E] mt-4 text-center">
              Escanea para dejar tu valoracion
            </p>
            <p className="text-xs text-[#78716C] mt-1 break-all text-center">
              {reviewUrl}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadQR}
              className="flex-1 rounded-full"
              data-testid="download-qr-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar PNG
            </Button>
            <Button
              onClick={handlePrintQR}
              className="flex-1 bg-[#C2410C] hover:bg-[#9A3412] rounded-full"
              data-testid="print-qr-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Review Card Component
const ReviewCard = ({ review, onUpdateStatus, onDelete, isPending }) => {
  const comment = review.comment_original || (review.comment ? Object.values(review.comment)[0] : '');
  const detectedLang = review.detected_language || 'unknown';
  const status = review.status || (review.verified ? 'approved' : 'pending');

  return (
    <div
      className={`p-4 rounded-lg border ${
        isPending ? 'bg-white border-yellow-200' : 'bg-[#FDFBF7] border-[#E5E5E5]'
      }`}
      data-testid={`review-card-${review.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        {/* Review Content */}
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-semibold text-[#1C1917]">{review.name}</span>
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-4 w-4 ${
                    i < review.rating
                      ? 'fill-[#F59E0B] text-[#F59E0B]'
                      : 'text-[#E7E5E4]'
                  }`}
                />
              ))}
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[status]}`}>
              {STATUS_LABELS[status]}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#E5E5E5] text-[#57534E] flex items-center gap-1">
              <Globe className="h-3 w-3" />
              {LANGUAGE_NAMES[detectedLang]}
            </span>
          </div>
          
          {comment && (
            <p 
              className="text-[#57534E] text-sm mb-2" 
              dir={detectedLang === 'ar' ? 'rtl' : 'ltr'}
            >
              "{comment}"
            </p>
          )}
          
          <div className="flex flex-wrap gap-4 text-xs text-[#78716C]">
            <span>Fecha: {review.date || new Date(review.created_at).toLocaleDateString()}</span>
            {review.email && <span>Email: {review.email}</span>}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {status === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={() => onUpdateStatus(review.id, 'approved')}
                className="bg-green-600 hover:bg-green-700"
              >
                <Check className="h-4 w-4 mr-1" />
                Aprobar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onUpdateStatus(review.id, 'rejected')}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <X className="h-4 w-4 mr-1" />
                Rechazar
              </Button>
            </>
          )}
          {status === 'approved' && (
            <Button
              size="sm"
              onClick={() => onUpdateStatus(review.id, 'featured')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Award className="h-4 w-4 mr-1" />
              Destacar
            </Button>
          )}
          {status === 'featured' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUpdateStatus(review.id, 'approved')}
            >
              Quitar destacado
            </Button>
          )}
          {status === 'rejected' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUpdateStatus(review.id, 'approved')}
            >
              Aprobar
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(review.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminReviews;
